import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import useOrganizationService from '../../../../app/services/hooks/useOrganizationService';
import useHandleErrorFields from '../../../../hooks/useHandleErrorFields';
import useReactConfirmAlert from '../../../../hooks/useReactConfirmAlert';
import DefaultCard from '../../../../components/default/DefaultCard';
import ButtonGeneral from '../../../../components/buttons/ButtonGeneral';
import ButtonSaveForm from '../../../../components/buttons/ButtonSaveForm';
import InputField from '../../../../components/InputField';
import TextAreaField from '../../../../components/TextAreaField';
import { formatDate } from '../../types';
import { OrganizationContextProps } from '../index';

interface EmailSummary {
    key: string;
    name: string;
    description: string;
    audience: 'client' | 'team';
    audience_display: string;
    default_subject: string;
    is_customized: boolean;
    updated_at: string | null;
}

interface EmailDetail extends EmailSummary {
    variables: { name: string; description: string }[];
    texts: { subject: string; intro: string; closing: string };
    preview: { subject: string; html: string };
    can_customize: boolean;
}

type TextField = 'subject' | 'intro' | 'closing';

/**
 * Correos del estudio: lista de todo lo que reciben los clientes y el equipo,
 * con vista previa y personalización de asunto, introducción y cierre.
 * Solo texto con variables; los datos del trámite y los botones los pone la plantilla.
 */
const EmailsTab: React.FC<OrganizationContextProps> = ({ organization, goToTab }) => {
    const {
        getEmailTemplates, getEmailTemplate, previewEmailTemplate, updateEmailTemplate, resetEmailTemplate,
        fetchingEmailTemplates, fetchingEmailTemplate, fetchingSaveEmailTemplate, fetchingPreviewEmailTemplate
    } = useOrganizationService();
    const { fieldErrorMessage, onFocusRemove, setErrors } = useHandleErrorFields();
    const { requestConfirmation } = useReactConfirmAlert();

    const [templates, setTemplates] = useState<EmailSummary[]>([]);
    const [canCustomize, setCanCustomize] = useState(false);
    const [selected, setSelected] = useState<EmailDetail | null>(null);
    const [texts, setTexts] = useState({ subject: '', intro: '', closing: '' });
    const [preview, setPreview] = useState<{ subject: string; html: string } | null>(null);
    const [lastField, setLastField] = useState<TextField>('intro');
    const previewTimer = useRef<ReturnType<typeof setTimeout>>();

    const loadList = (selectKey?: string) => {
        getEmailTemplates({
            onSuccess: (response) => {
                setTemplates(response.data.templates);
                setCanCustomize(response.data.can_customize);
                const key = selectKey || selected?.key || response.data.templates[0]?.key;
                if (key && !selected) openTemplate(key);
            },
            onError: (response) => toast.error(response.message)
        });
    };

    // Al cambiar de correo se descarta la vista previa pendiente del anterior
    const selectedKeyRef = useRef<string | null>(null);

    const openTemplate = (key: string) => {
        if (previewTimer.current) clearTimeout(previewTimer.current);
        selectedKeyRef.current = key;
        getEmailTemplate(key, {
            onSuccess: (response) => {
                if (selectedKeyRef.current !== key) return;
                setSelected(response.data);
                setTexts(response.data.texts);
                setPreview(response.data.preview);
                setErrors({});
            },
            onError: (response) => toast.error(response.message)
        });
    };

    useEffect(() => {
        loadList();
        return () => {
            if (previewTimer.current) clearTimeout(previewTimer.current);
        };
    }, []);

    // Vista previa en vivo (con pausa para no pedir una por tecla)
    const schedulePreview = (next: typeof texts) => {
        if (!selected) return;
        if (previewTimer.current) clearTimeout(previewTimer.current);
        const key = selected.key;
        previewTimer.current = setTimeout(() => {
            previewEmailTemplate(key, next, {
                onSuccess: (response) => {
                    // Respuesta de un correo que ya no está abierto
                    if (selectedKeyRef.current !== key) return;
                    setPreview(response.data.preview);
                    setErrors({});
                },
                onFieldError: (response) => {
                    if (selectedKeyRef.current === key) setErrors(response.data);
                }
            });
        }, 600);
    };

    const setText = (field: TextField) => (value: string) => {
        const next = { ...texts, [field]: value };
        setTexts(next);
        schedulePreview(next);
    };

    const insertVariable = (name: string) => {
        const token = `{{${name}}}`;
        const current = texts[lastField] || '';
        setText(lastField)(current ? `${current} ${token}` : token);
    };

    const handleSave = () => {
        if (!selected) return;
        updateEmailTemplate(selected.key, texts, {
            onSuccess: (response) => {
                toast.success(response.message);
                setPreview(response.data.preview);
                setErrors({});
                setTemplates((list) => list.map((t) => (t.key === selected.key ? { ...t, is_customized: response.data.is_customized, updated_at: response.data.updated_at } : t)));
            },
            onError: (response) => toast.error(response.message),
            onFieldError: (response) => {
                setErrors(response.data);
                toast.error('Revisa los textos marcados');
            }
        });
    };

    const handleReset = () => {
        if (!selected) return;
        requestConfirmation({
            title: '¿Volver al correo original?',
            message: 'Se borra tu asunto, introducción y cierre de este correo.',
            buttons: {
                confirmButton: {
                    label: 'Sí, restaurar',
                    onClick: () =>
                        resetEmailTemplate(selected.key, {
                            onSuccess: (response) => {
                                toast.success(response.message);
                                setTexts(response.data.texts);
                                setPreview(response.data.preview);
                                setTemplates((list) => list.map((t) => (t.key === selected.key ? { ...t, is_customized: false } : t)));
                            },
                            onError: (response) => toast.error(response.message)
                        })
                },
                cancelButton: { label: 'Cancelar' }
            }
        });
    };

    // Mientras llega un correo recién abierto no se edita: su respuesta reemplazaría lo escrito
    const editable = canCustomize && organization.status === 'active' && !fetchingEmailTemplate;
    const groups: { title: string; items: EmailSummary[] }[] = [
        { title: 'A tus clientes', items: templates.filter((t) => t.audience === 'client') },
        { title: 'A tu equipo', items: templates.filter((t) => t.audience === 'team') }
    ];

    return (
        <div className="row">
            <div className="col-lg-4 mb-3">
                <DefaultCard>
                    <h5 className="mb-1">Correos del estudio</h5>
                    <p className="text-muted small">
                        Todo lo que la plataforma envía a tu nombre. Elige uno para verlo y personalizarlo.
                    </p>
                    {fetchingEmailTemplates && templates.length === 0 && <div className="text-center py-3"><div className="spinner-border text-primary" /></div>}
                    {groups.map((group) => group.items.length > 0 && (
                        <div key={group.title} className="mb-3">
                            <div className="text-uppercase text-muted small fw-bold mb-2">{group.title}</div>
                            <div className="list-group" data-testid="email-template-list">
                                {group.items.map((template) => (
                                    <button
                                        key={template.key}
                                        type="button"
                                        className={`list-group-item list-group-item-action ${selected?.key === template.key ? 'active' : ''}`}
                                        onClick={() => openTemplate(template.key)}
                                        data-testid={`email-template-${template.key}`}
                                    >
                                        <div className="d-flex justify-content-between align-items-center">
                                            <span className="fw-bold small">{template.name}</span>
                                            {template.is_customized && <span className="badge bg-success ms-2">Personalizado</span>}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </DefaultCard>
            </div>

            <div className="col-lg-8 mb-3">
                <DefaultCard>
                    {!canCustomize && (
                        <div className="alert alert-info small d-flex justify-content-between align-items-center gap-2">
                            <span>Puedes ver tus correos. Para personalizarlos necesitas un plan con marca propia.</span>
                            <ButtonGeneral onClick={() => goToTab('subscription')} title="Ver planes" size="small" variant="outline" />
                        </div>
                    )}

                    {fetchingEmailTemplate && !selected && <div className="text-center py-5"><div className="spinner-border text-primary" /></div>}

                    {selected && (
                        <>
                            <div className="d-flex justify-content-between align-items-start mb-3">
                                <div>
                                    <h5 className="mb-1">{selected.name}</h5>
                                    <p className="text-muted small mb-0">{selected.description}</p>
                                    {selected.updated_at && <small className="text-muted">Editado el {formatDate(selected.updated_at)}</small>}
                                </div>
                                <span className="badge bg-light text-dark border">{selected.audience_display}</span>
                            </div>

                            <div className="row">
                                <div className="col-12 mb-3">
                                    <InputField label="Asunto" value={texts.subject} disabled={!editable}
                                                placeholder={selected.default_subject}
                                                onChange={setText('subject')}
                                                onFocus={() => { setLastField('subject'); onFocusRemove('subject'); }}
                                                error={fieldErrorMessage('subject')} />
                                </div>
                                <div className="col-12 mb-3">
                                    <TextAreaField label="Introducción (va después del saludo)" value={texts.intro} disabled={!editable}
                                                   rows={3} maxLength={2000} placeholder="Ej.: Gracias por confiar en {{estudio}}."
                                                   onChange={setText('intro')}
                                                   onFocus={() => { setLastField('intro'); onFocusRemove('intro'); }}
                                                   error={fieldErrorMessage('intro')} />
                                </div>
                                <div className="col-12 mb-2">
                                    <TextAreaField label="Cierre o firma" value={texts.closing} disabled={!editable}
                                                   rows={3} maxLength={1000} placeholder={'Saludos,\nEquipo {{estudio}}'}
                                                   onChange={setText('closing')}
                                                   onFocus={() => { setLastField('closing'); onFocusRemove('closing'); }}
                                                   error={fieldErrorMessage('closing')} />
                                </div>
                                <div className="col-12 mb-3">
                                    <small className="text-muted d-block mb-1">Variables (se insertan en el último campo que tocaste):</small>
                                    <div className="d-flex flex-wrap gap-1">
                                        {selected.variables.map((variable) => (
                                            <button key={variable.name} type="button" className="btn btn-sm btn-outline-secondary"
                                                    title={variable.description} disabled={!editable}
                                                    onClick={() => insertVariable(variable.name)}>
                                                {`{{${variable.name}}}`}
                                            </button>
                                        ))}
                                    </div>
                                    <small className="text-muted d-block mt-2">
                                        Solo texto: el diseño, los datos del trámite y los enlaces los agrega la plataforma.
                                    </small>
                                </div>
                            </div>

                            {editable && (
                                <div className="d-flex justify-content-end gap-2 mb-3">
                                    {selected.is_customized && (
                                        <ButtonGeneral onClick={handleReset} title="Restaurar original" variant="outline" size="small" icon="fa-undo" />
                                    )}
                                    <ButtonSaveForm callbackSave={handleSave} loading={fetchingSaveEmailTemplate} title="Guardar correo" />
                                </div>
                            )}

                            <div className="border rounded">
                                <div className="bg-light px-3 py-2 small border-bottom d-flex justify-content-between">
                                    <span><strong>Asunto:</strong> {preview?.subject}</span>
                                    {fetchingPreviewEmailTemplate && <span className="spinner-border spinner-border-sm text-secondary" />}
                                </div>
                                <iframe
                                    title="Vista previa del correo"
                                    srcDoc={preview?.html || ''}
                                    sandbox=""
                                    style={{ width: '100%', height: 560, border: 0, background: '#f4f4f4' }}
                                    data-testid="email-preview"
                                />
                            </div>
                            <small className="text-muted">Vista previa con datos de ejemplo.</small>
                        </>
                    )}
                </DefaultCard>
            </div>
        </div>
    );
};

export default EmailsTab;
